import React, { useContext, useMemo } from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import MobileStepper from '@mui/material/MobileStepper';
import Button from '@mui/material/Button';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { MyCostPredictionSummary } from './MyCostPredictionSummary';
import { useTranslation } from "react-i18next";
import { UcWidget } from '../common/UcWidget';
import { getAccountPopoverTitle, AccountContext } from '../../lib/AccountProvider';
import EnsureUserAndModule from 'components/auth/EnsureUserAndModule';
import { UcMeterSelectModular } from '../common/UcMeterSelectModular';
import { useResponsive } from '../../lib/useResponsive';
// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { useMyTimezone } from "components/customer/orgs/MyOrgAPIEndpoints";

export const MyCostPredictionPage = () => {
    const theme = useTheme();
    const { account } = useContext(AccountContext);
    const [currentBill, setCurrentBill] = React.useState(true);
    const [uom, setUom] = React.useState("KWH");
    const popoverTitle = getAccountPopoverTitle(account);
    const { isMobile, isTablet } = useResponsive();
    const timezone = useMyTimezone();

    const today = React.useMemo(() => DateTime.now().minus({ days: 1 }), []);
    const startDate = React.useMemo(() =>
        DateTime.fromObject({ year: today.year, month: today.month, day: today.day }, { zone: timezone })
            .startOf("month").toJSDate(), [timezone, today]);

    const endDate = React.useMemo(() =>
        DateTime.fromObject({ year: today.year, month: today.month, day: today.day }, { zone: timezone })
            .endOf("month").toJSDate(), [timezone, today]);

    const { t } = useTranslation();

    const handleNext = () => {
        setCurrentBill(false);
    };

    const handleBack = () => {
        setCurrentBill(true);
    };

    const handleChangeCommodityType = (meterOption) => {
        setUom(meterOption.uom);
    };

    const steps = useMemo(() => {
        return [
            {
                component: (
                    <MyCostPredictionSummary
                        key={currentBill}
                        currentBill={currentBill}
                        uom={uom}
                    />
                )
            }
        ];
    }, [currentBill, uom]);

    const actionItems = [
        {
            component: (
                <UcMeterSelectModular
                    startDate={startDate}
                    endDate={endDate}
                    uom={uom}
                    onlyCommodity={true}
                    forcedWidth={(!isMobile && !isTablet) ? '50%' : '100%'}
                    onChange={handleChangeCommodityType}
                />
            )
        },
    ]

    const title = t("Cost Prediction") + " - " + account?.accountName + " - " + account?.accountAddress

    return (
        <EnsureUserAndModule modules={['Cost']}>
            <UcWidget
                title={title}
                subTitle={t("* Cost estimation does not take into account taxes/fees.")}
                subTitleCypressIdSuffix={'cost-prediction-page'}
                actionItems={actionItems}
                contentPadding='0 0 0 0'
                alignTitleLeft={true}
                popoverTitle={popoverTitle}
                >
            <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ width: '100%', height:'100%', p: 2 }}>
                    {steps[0].component}
                </Box>
                <MobileStepper
                    steps={2}
                    position="static"
                    activeStep={currentBill ? 0 : 1}
                    nextButton={
                        <Button
                            size="small"
                            onClick={handleNext}
                            disabled={!currentBill}
                        >
                            {t("Next Bill")}
                            {theme.direction === 'rtl' ? (
                                <KeyboardArrowLeft />
                            ) : (
                                <KeyboardArrowRight />
                            )}
                        </Button>
                    }
                    backButton={
                        <Button
                            size="small"
                            onClick={handleBack}
                            disabled={currentBill}>
                            {theme.direction === 'rtl' ? (
                                <KeyboardArrowRight />
                            ) : (
                                <KeyboardArrowLeft />
                            )}
                            {t("Current Bill")}
                        </Button>
                    }
                />
                </Box>
            </UcWidget>
        </EnsureUserAndModule>
    );
}